import { ProfOfferFlat, RentLivingTenantRequirements } from '@search/graphql-typings';
import {
    getRenovationType,
    getTotalArea,
    getLivingArea,
    getKitchenArea,
    getFloor,
    getBathroomB2b,
    getBathroomB2c,
    getBalcony,
    getLivingFacilities,
    getRoomsCountInFlat,
    getTenantsInfoB2b,
    getTenantsInfoB2c,
    getApartments,
    getWindowsDirection
} from './infoHelpers';
import { mapToInformations, InfoModelRaw, InformationsEnum, InformationsIconEnum, TGenerateSeoUrlParams } from './infoHelpers/types/InfoModel';

export const buildInfoFlat = (
    flat: ProfOfferFlat,
    rentTenant?: RentLivingTenantRequirements | null,
    params?: {
        withApartment?: boolean;
        useB2cFuncs?: boolean;
        generateUrl?: (params: TGenerateSeoUrlParams) => string;
        isNewBuilding?: boolean;
    },
    isMobile?: boolean
) => {
    const totalArea = getTotalArea(flat);
    const livingArea = getLivingArea(flat);
    const kitchenArea = getKitchenArea(flat);
    const floorInfo = getFloor(flat);
    const balcony = getBalcony(flat);
    const roomsCountInFlat = getRoomsCountInFlat(flat);
    const apartment = params?.withApartment && getApartments(flat);

    const informationsRaw: InfoModelRaw[] = [
        ...(apartment ? [ {
            title: 'Тип жилья',
            value: 'апартаменты',
            key: InformationsEnum.apartments
        } ] : []),
        ...(roomsCountInFlat ? [ {
            title: isMobile ? 'Квартира' : 'Комнат',
            value: isMobile ? `${roomsCountInFlat}-комнатная` : roomsCountInFlat,
            key: InformationsEnum.roomsCountInFlat
        } ] : []),
        ...getRenovationType(flat, params?.isNewBuilding),
        {
            title: 'Площадь квартиры',
            value: totalArea,
            key: InformationsEnum.totalArea
        },
        {
            title: 'Жилая площадь',
            value: livingArea,
            key: InformationsEnum.livingArea
        },
        {
            title: 'Площадь кухни',
            value: kitchenArea,
            key: InformationsEnum.kitchenArea,
            icon: InformationsIconEnum.tertiaryArea
        },
        {
            title: 'Этаж',
            value: isMobile ? floorInfo?.floorText2 : floorInfo?.floorText,
            key: InformationsEnum.floor,
            extraSeoParams: {
                ...floorInfo
            }
        },
        ...(params?.useB2cFuncs ? getBathroomB2c(flat) : getBathroomB2b(flat)),
        {
            title: 'Балкон/лоджия',
            value: balcony,
            key: InformationsEnum.balcony
        },
        ...getLivingFacilities(flat.livingFacilities),
        ...(params?.useB2cFuncs ? getTenantsInfoB2c(rentTenant) : getTenantsInfoB2b(rentTenant)),
        ...getWindowsDirection(flat)
    ];

    return mapToInformations(informationsRaw, params);
};

export const mainBuildInfoFlat = (
    flat: ProfOfferFlat,
    rentTenant?: RentLivingTenantRequirements | null,
    params?: {
        withApartment?: boolean;
        useB2cFuncs?: boolean;
        generateUrl?: (params: TGenerateSeoUrlParams) => string;
        isNewBuilding?: boolean;
    },
    isMobile?: boolean
) => {
    const totalArea = getTotalArea(flat);
    const livingArea = getLivingArea(flat);
    const kitchenArea = getKitchenArea(flat);
    const roomsCountInFlat = getRoomsCountInFlat(flat);
    const floorInfo = getFloor(flat);
    const apartment = params?.withApartment && getApartments(flat);

    const infoRaw: InfoModelRaw[] = isMobile ? [
        ...(roomsCountInFlat ? [ {
            title: 'Квартира',
            value: `${roomsCountInFlat}-комн.`,
            key: InformationsEnum.roomsCountInFlat
        } ] : []),
        {
            title: 'Площадь',
            value: totalArea,
            key: InformationsEnum.totalArea
        },
        {
            title: 'Кухня',
            value: kitchenArea,
            key: InformationsEnum.kitchenArea,
            icon: InformationsIconEnum.tertiaryArea
        },
        {
            title: 'Этаж',
            value: floorInfo?.floorText2,
            key: InformationsEnum.floor,
            extraSeoParams: {
                ...floorInfo
            }
        },
    ] : [
        ...(apartment ? [ {
            title: 'Тип жилья',
            value: 'апартаменты',
            key: InformationsEnum.apartments
        } ] : []),
        ...(roomsCountInFlat ? [ {
            title: 'Комнатность',
            value: `${roomsCountInFlat}-комнатная`,
            key: InformationsEnum.roomsCountInFlat
        } ] : []),
        {
            title: 'Площадь квартиры',
            value: totalArea,
            key: InformationsEnum.totalArea
        },
        {
            title: 'Жилая площадь',
            value: livingArea,
            key: InformationsEnum.livingArea
        },
        {
            title: 'Площадь кухни',
            value: kitchenArea,
            key: InformationsEnum.kitchenArea,
            icon: InformationsIconEnum.tertiaryArea
        },
        {
            title: 'Этаж',
            value: floorInfo?.floorText,
            key: InformationsEnum.floor,
            extraSeoParams: {
                ...floorInfo
            }
        },
    ];

    return mapToInformations(infoRaw, params);
};

export const additionalBuildInfoFlat = (
    flat: ProfOfferFlat,
    rentTenant?: RentLivingTenantRequirements | null,
    params?: {
        withApartment?: boolean;
        useB2cFuncs?: boolean;
        generateUrl?: (params: TGenerateSeoUrlParams) => string;
        isNewBuilding?: boolean;
    }
) => {
    const balcony = getBalcony(flat);

    const infoRaw: InfoModelRaw[] = [
        ...(params?.useB2cFuncs ? getBathroomB2c(flat) : getBathroomB2b(flat)),
        {
            title: 'Балкон/лоджия',
            value: balcony,
            key: InformationsEnum.balcony
        },
        ...getRenovationType(flat, params?.isNewBuilding),
        ...getLivingFacilities(flat.livingFacilities),
        ...(params?.useB2cFuncs ? getTenantsInfoB2c(rentTenant) : getTenantsInfoB2b(rentTenant)),
        ...getWindowsDirection(flat)
    ];

    return mapToInformations(infoRaw, params);
};
